import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { MaturityComparisonSubject } from '../../graphql/types';
import type { SpiderChartCategory } from './MaturitySpiderChart';
import './MaturityComparisonChart.css';

export interface MaturityComparisonChartProps {
  subjects: MaturityComparisonSubject[];
  categories: SpiderChartCategory[];
  size?: number;
}

const MAX_LEVEL = 6;
const LEVELS = 6;

// Colorblind-safe multi-subject palette (distinct from single-subject chart)
const SUBJECT_COLORS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b'];

export function MaturityComparisonChart({
  subjects,
  categories,
  size = 400,
}: MaturityComparisonChartProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || categories.length === 0 || subjects.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = 80;
    const radius = size / 2 - margin;
    const cx = size / 2;
    const cy = size / 2;
    const total = categories.length;
    const angleSlice = (Math.PI * 2) / total;

    const rScale = d3.scaleLinear().range([0, radius]).domain([0, MAX_LEVEL]);

    const g = svg.append('g').attr('transform', `translate(${cx},${cy})`);

    // Grid circles
    for (let level = 1; level <= LEVELS; level++) {
      g.append('circle')
        .attr('r', rScale(level))
        .attr('fill', 'none')
        .attr('stroke', 'var(--forge-theme-border, #e0e0e0)')
        .attr('stroke-width', level === MAX_LEVEL ? 1 : 0.5)
        .attr('stroke-dasharray', level < MAX_LEVEL ? '2,3' : null);

      g.append('text')
        .attr('x', 4)
        .attr('y', -rScale(level) + 1)
        .attr('dy', '0.35em')
        .attr('font-size', '9px')
        .attr('fill', 'var(--forge-theme-text-medium, #757575)')
        .text(String(level));
    }

    // Axes and labels
    categories.forEach((cat, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      const x = rScale(MAX_LEVEL) * Math.cos(angle);
      const y = rScale(MAX_LEVEL) * Math.sin(angle);

      g.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', x)
        .attr('y2', y)
        .attr('stroke', 'var(--forge-theme-border, #e0e0e0)')
        .attr('stroke-width', 0.7);

      const labelR = rScale(MAX_LEVEL) + 20;
      const lx = labelR * Math.cos(angle);
      const ly = labelR * Math.sin(angle);
      const label = cat.name.length > 16 ? cat.name.slice(0, 15) + '…' : cat.name;

      g.append('text')
        .attr('x', lx)
        .attr('y', ly)
        .attr('dy', '0.35em')
        .attr('text-anchor', lx < -2 ? 'end' : lx > 2 ? 'start' : 'middle')
        .attr('font-size', '10px')
        .attr('fill', 'var(--forge-theme-text-primary, #212121)')
        .text(label);
    });

    // Draw each subject polygon
    subjects.forEach((subject, si) => {
      const color = SUBJECT_COLORS[si % SUBJECT_COLORS.length];
      const scoreMap = new Map(subject.categoryScores.map((s) => [s.categoryId, s.score]));

      const points = categories.map((cat, i) => {
        const angle = angleSlice * i - Math.PI / 2;
        const score = scoreMap.get(cat.id) ?? 0;
        return {
          x: rScale(score) * Math.cos(angle),
          y: rScale(score) * Math.sin(angle),
          score,
          cat,
        };
      });

      // Polygon fill (semi-transparent)
      g.append('polygon')
        .attr('points', points.map((p) => `${p.x},${p.y}`).join(' '))
        .attr('fill', color)
        .attr('fill-opacity', 0.08)
        .attr('stroke', color)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', si > 0 ? '4,2' : null)
        .attr('stroke-linejoin', 'round');

      // Dot per category
      points.forEach((p) => {
        const dotG = g.append('g').attr('class', 'comparison-dot');
        dotG
          .append('circle')
          .attr('cx', p.x)
          .attr('cy', p.y)
          .attr('r', 4)
          .attr('fill', 'white')
          .attr('stroke', color)
          .attr('stroke-width', 1.5);
        dotG.append('circle').attr('cx', p.x).attr('cy', p.y).attr('r', 2).attr('fill', color);
        dotG
          .append('title')
          .text(
            `${subject.subjectName} — ${p.cat.name}: ${p.score > 0 ? p.score.toFixed(1) : 'Not scored'}`
          );
      });
    });
  }, [subjects, categories, size]);

  // Build legend outside SVG for accessibility
  const legendItems = subjects.map((s, i) => ({
    label: s.subjectName,
    color: SUBJECT_COLORS[i % SUBJECT_COLORS.length],
    score: s.overallScore,
  }));

  return (
    <div className="maturity-comparison-chart">
      <svg
        ref={svgRef}
        width={size}
        height={size}
        role="img"
        aria-label="Maturity comparison radar chart showing multiple subjects"
      />
      {legendItems.length > 0 && (
        <ul className="maturity-comparison-chart__legend" aria-label="Subject legend">
          {legendItems.map((item) => (
            <li key={item.label} className="maturity-comparison-chart__legend-item">
              <span
                className="maturity-comparison-chart__legend-swatch"
                style={{ background: item.color }}
                aria-hidden="true"
              />
              <span className="maturity-comparison-chart__legend-label">{item.label}</span>
              <span className="maturity-comparison-chart__legend-score">
                {item.score.toFixed(1)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
